import { useState } from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { startCheckout, type PaidTier } from '@/lib/billing';
import { PRICING_DISPLAY, SALES_CONTACT_MAILTO } from '@/lib/waitlist';

export interface UpgradeModalProps {
  open: boolean;
  /** Short message explaining why this modal is shown (e.g. "You've hit your sends limit"). */
  context?: string;
  /** Which paid tier to upsell — defaults to Pro. */
  defaultTier?: PaidTier;
  /** Current tier — when on Pro we lead with Scale; on Scale we lead with Custom only. */
  currentTier?: 'free' | 'pro' | 'scale';
  onClose: () => void;
}

/**
 * Generic "you hit a limit" modal. Renders the two paid tiers as side-by-side
 * pickers + a Custom (Talk to us) fallback. The CTA does a server round-trip
 * to create a Stripe Checkout Session and redirects.
 *
 * Replaces the old WaitlistModal in over-quota contexts (NewDraft generate
 * limit, Editor AI-edit limit, SendModal send limit). The WaitlistModal still
 * exists for non-billing-gated waitlist signups elsewhere if we need it.
 */
export function UpgradeModal({
  open,
  context,
  defaultTier = 'pro',
  currentTier = 'free',
  onClose,
}: UpgradeModalProps) {
  const [pending, setPending] = useState<PaidTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  // On Pro → only show Scale upgrade (Pro tile is "your current plan").
  // On Scale → no checkout target, just Custom.
  const canUpgradePro = currentTier === 'free';
  const canUpgradeScale = currentTier !== 'scale';

  async function handleUpgrade(tier: PaidTier) {
    setError(null);
    setPending(tier);
    try {
      await startCheckout(tier);
    } catch (err) {
      setPending(null);
      setError(err instanceof ApiError ? err.message : 'Could not start checkout. Try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] gap-5 overflow-y-auto rounded-2xl sm:max-w-lg">
        <DialogHeader className="text-left">
          <DialogTitle>Upgrade your plan</DialogTitle>
          {context ? (
            <DialogDescription className="text-muted-foreground">{context}</DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {canUpgradePro ? (
            <UpgradeTierOption
              tier="pro"
              recommended={defaultTier === 'pro'}
              loading={pending === 'pro'}
              onSelect={() => handleUpgrade('pro')}
            />
          ) : null}
          {canUpgradeScale ? (
            <UpgradeTierOption
              tier="scale"
              recommended={defaultTier === 'scale' || !canUpgradePro}
              loading={pending === 'scale'}
              onSelect={() => handleUpgrade('scale')}
            />
          ) : null}
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="border-border border-t pt-4">
          <p className="text-muted-foreground text-xs">
            Need higher volumes, dedicated IPs, or a self-hosted deployment?{' '}
            <a
              className="text-foreground hover:text-primary underline"
              href={SALES_CONTACT_MAILTO}
            >
              Talk to us about a custom plan →
            </a>
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={!!pending}>
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeTierOption({
  tier,
  recommended,
  loading,
  onSelect,
}: {
  tier: PaidTier;
  recommended: boolean;
  loading: boolean;
  onSelect: () => void;
}) {
  const plan = PRICING_DISPLAY[tier];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={loading}
      className={
        'group focus-visible:ring-ring/40 relative flex h-full flex-col rounded-xl border p-4 text-left transition hover:shadow-sm focus-visible:ring-2 focus-visible:outline-none ' +
        (recommended
          ? 'border-primary bg-primary/[0.03]'
          : 'border-border bg-background hover:border-foreground/20')
      }
    >
      {recommended ? (
        <span className="bg-primary text-primary-foreground absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
          Recommended
        </span>
      ) : null}

      <div className="flex items-baseline justify-between">
        <span className="font-display text-base font-semibold">{plan.name}</span>
        <span className="text-foreground text-sm">
          ${plan.price}
          <span className="text-muted-foreground"> / mo</span>
        </span>
      </div>

      <ul className="text-muted-foreground mt-3 space-y-1 text-xs">
        {plan.features.slice(0, 3).map((f) => (
          <li key={f} className="flex items-start gap-1.5">
            <span className="text-success mt-[2px]">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="text-foreground mt-4 inline-flex items-center gap-1.5 text-sm font-medium">
        {loading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <ArrowUpRight className="size-3.5" />
        )}
        {loading ? 'Redirecting…' : `Upgrade to ${plan.name}`}
      </div>
    </button>
  );
}
